/**
 * Widget embed format presets (ADR-011 Decision 3).
 *
 * The /widget/:companySlug route accepts a `?layout=` query param that picks
 * one of three PRESENTATION-ONLY presets. Validation rules are Corridor
 * guardrails and non-negotiable:
 *
 *   - `layout` is validated against the CLOSED enum below; any unknown value
 *     falls back to `full`. The raw value is never reflected into markup,
 *     attributes, analytics labels, or error text — downstream code only ever
 *     sees a member of {@link WIDGET_LAYOUTS}.
 *   - `rideType` (optional preselect) is validated against the company's
 *     server-fetched ride-type list; unknown ids are silently ignored. It only
 *     pre-answers the wizard's ride-type step — the public create endpoint
 *     re-validates the id server-side regardless.
 *   - Presets never bypass server-side gates: `allowEmbeddableWidget` and all
 *     FeatureGateService assertions stay server-asserted.
 */

export const WIDGET_LAYOUTS = ["full", "compact", "inline"] as const;

export type WidgetLayout = (typeof WIDGET_LAYOUTS)[number];

/**
 * Validate the raw `?layout=` query value against the closed enum.
 * @param raw - The raw query-param value (or null when absent).
 * @returns The validated layout; `full` for anything unknown.
 */
export function parseWidgetLayout(raw: string | null): WidgetLayout {
  return (WIDGET_LAYOUTS as readonly string[]).includes(raw ?? "")
    ? (raw as WidgetLayout)
    : "full";
}

/** Minimal ride-type shape shared with the booking flow's catalog query. */
export interface WidgetRideType {
  id: string;
  name: string;
  description: string | null;
  type: string;
}

/**
 * Resolve the optional `?rideType=` preselect param against the company's
 * server-fetched ride-type list (allow-list validation — the raw value is
 * never consumed directly and never reflected).
 *
 * Returns null when the param is absent, unknown, or when the company offers
 * fewer than two ride types — with a single ride type the wizard already
 * auto-selects it and skips the Ride Type step, so a preselect would
 * mis-index the handoff step.
 * @param raw - The raw query-param value (or null when absent).
 * @param rideTypes - The company's server-fetched ride types.
 * @returns The matching ride type, or null when the param must be ignored.
 */
export function resolvePreselectedRideType(
  raw: string | null,
  rideTypes: readonly WidgetRideType[]
): WidgetRideType | null {
  if (!raw || rideTypes.length < 2) return null;
  return rideTypes.find((rt) => rt.id === raw) ?? null;
}

/**
 * The handoff WidgetLayout passes to BookingPage (via outlet context) when a
 * preset pre-answers the ride-type step — either the compact teaser's explicit
 * tap or a validated `?rideType=` preselect.
 */
export interface WidgetHandoff {
  /** Validated against the company's ride-type list before construction. */
  rideTypeId: string;
  /** Optional ISO pickup datetime from the compact teaser's date affordance. */
  pickupDatetime?: string;
  /**
   * True when the handoff is an explicit user action (teaser tap) that should
   * win over a previously saved draft's ride type; false for the static
   * `?rideType=` snippet param, which defers to an in-progress draft.
   */
  override: boolean;
}
